// SEQ-180 E22: admin-only unmapped data screen. There is no existing
// in-page, admin-only SECTION pattern in this codebase to copy: every
// admin-only surface here (Team, Organization, Settings) is a whole route
// gated by <RoleGuard requiredRole="admin"> (src/App.tsx), not a section
// inside a shared page. Since this content belongs on the analytics page
// itself (it is meaningless without the page's own data), it is gated the
// same way RoleGuard and the sidebar already compute admin access:
// `isAdmin || isSuperAdmin` from useAuth(), collapsed by default so a
// non-admin viewer of this component (it still only renders for admins)
// does not see it by default on every visit.
import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, ShieldAlert, MapPin, Tag, RefreshCw, FileWarning } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useToast } from "../ui/ToastModal";
import { supabase } from "../../lib/supabase";
import { pnlChartCardStyle } from "../../features/pnlAnalytics/pnlAnalyticsFormat";
import { refreshErrorMessage } from "../../features/pnlAnalytics/refreshMessages";
import {
  describeOrphanTotals,
  summariseOrphanCategoryValues,
  type OrphanCategorySummary,
  type OrphanCategoryValueRow,
} from "../../features/pnlAnalytics/orphanCategoryValues";

interface UnmappedLocation {
  id: string;
  keypay_location_name: string;
  match_method: string;
}

interface PnlAdminUnmappedProps {
  unmappedCategoryStrings: string[];
  projects: { id: string; name: string }[];
  /** Re-fetches the page's own analytics data (e.g. the "payroll as at" date) after a successful refresh. */
  onPayrollRefreshed?: () => void;
}

/* ORPHAN CATEGORY VALUES (14 September 2026).
 *
 * The list below it, "Unmapped category strings", answers a different
 * question: which strings fail to resolve to a metric key. A category
 * removed in the editor usually still resolves, because an alias row was
 * backfilled for it, so it never appeared there while having no row on any
 * P&L to show it. That is the state the two Aire - Newstead rows were in,
 * and only comparing every value row against the configured list finds it.
 *
 * Reads public.pnl_orphan_category_values
 * (supabase/migrations/20260914000600_pnl_orphan_category_values.sql), a
 * security_invoker view, so this shows only the caller's own organisation
 * and only projects they can see. That is one more reason this section is
 * admin gated: claims_pnl_monthly_values' SELECT policy shows a non-admin
 * only their own projects, so their list would be incomplete. */
async function fetchOrphanCategoryValues(organizationId: string) {
  /* eslint-disable @typescript-eslint/no-explicit-any -- pnl_orphan_category_values is a new view, not in the generated Supabase types */
  return (supabase as any)
    .from("pnl_orphan_category_values")
    .select("category, source, project_name, month_date, amount")
    .eq("organization_id", organizationId)
    .order("category", { ascending: true })
    .order("month_date", { ascending: true }) as Promise<{
      data: { category: string; source: string; project_name: string | null; month_date: string; amount: number | string }[] | null;
      error: { message: string } | null;
    }>;
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

// Pure fetch, no setState: shared by the expand-triggered effect and the
// manual "Refresh payroll data" button, each of which applies the result to
// its own component state directly rather than through a shared setter, so
// neither call site trips the "no setState inside an effect body" lint rule.
async function fetchUnmappedLocationRows(organizationId: string) {
  /* eslint-disable @typescript-eslint/no-explicit-any -- pnl_payroll_unmapped_locations is not yet in generated Supabase types */
  return (supabase as any)
    .from("pnl_payroll_unmapped_locations")
    .select("id, keypay_location_name, match_method")
    .eq("organization_id", organizationId)
    .order("keypay_location_name", { ascending: true }) as Promise<{ data: UnmappedLocation[] | null; error: { message: string } | null }>;
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

export function PnlAdminUnmapped({ unmappedCategoryStrings, projects, onPayrollRefreshed }: PnlAdminUnmappedProps) {
  const { isAdmin, isSuperAdmin, organizationId, orgDetails } = useAuth();
  const { addToast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [locations, setLocations] = useState<UnmappedLocation[]>([]);
  // Starts true (not false): the collapsed summary reads this to decide
  // between a "loading" placeholder and the real count, and the real count
  // is not known until the first fetch (on expand) completes, so it must
  // not default to the "loaded, count 0" state before that fetch has run.
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [selection, setSelection] = useState<Record<string, string>>({});
  const [refreshingPayroll, setRefreshingPayroll] = useState(false);
  const [orphans, setOrphans] = useState<OrphanCategorySummary[]>([]);
  // Same reasoning as `loading` above: unknown until the first fetch, and the
  // collapsed summary must not read "none" before anything has been asked.
  const [orphansLoading, setOrphansLoading] = useState(true);

  const canSeeAdmin = isAdmin || isSuperAdmin;

  useEffect(() => {
    if (!canSeeAdmin || !expanded || !organizationId) return;
    let cancelled = false;

    async function loadUnmappedLocations() {
      setLoading(true);
      const { data, error } = await fetchUnmappedLocationRows(organizationId as string);
      if (!cancelled) {
        if (error) {
          addToast({ type: "error", title: "Could not load unmapped locations", message: error.message });
        } else {
          setLocations(data ?? []);
        }
        setLoading(false);
      }
    }

    async function loadOrphanCategoryValues() {
      setOrphansLoading(true);
      const { data, error } = await fetchOrphanCategoryValues(organizationId as string);
      if (cancelled) return;
      if (error) {
        addToast({ type: "error", title: "Could not load orphaned category values", message: error.message });
      } else {
        const rows: OrphanCategoryValueRow[] = (data ?? []).map((row) => ({
          category: row.category,
          source: row.source,
          projectName: row.project_name ?? "an unnamed project",
          monthDate: row.month_date,
          amount: Number(row.amount) || 0,
        }));
        setOrphans(summariseOrphanCategoryValues(rows));
      }
      setOrphansLoading(false);
    }

    loadUnmappedLocations();
    loadOrphanCategoryValues();
    return () => {
      cancelled = true;
    };
  }, [canSeeAdmin, expanded, addToast, organizationId]);

  if (!canSeeAdmin) return null;

  const refreshPayroll = async () => {
    if (!organizationId) return;
    setRefreshingPayroll(true);
    /* eslint-disable @typescript-eslint/no-explicit-any -- refresh_pnl_overtime_monthly is not yet in generated Supabase types */
    const { error } = await (supabase as any).rpc("refresh_pnl_overtime_monthly");
    /* eslint-enable @typescript-eslint/no-explicit-any */

    if (error) {
      setRefreshingPayroll(false);
      addToast({ type: "error", title: "Could not refresh payroll data", message: refreshErrorMessage(error) });
      return;
    }

    addToast({ type: "success", title: "Payroll data refreshed" });
    const { data: refreshedData, error: reloadError } = await fetchUnmappedLocationRows(organizationId);
    if (!reloadError) {
      setLocations(refreshedData ?? []);
    }
    setRefreshingPayroll(false);
    onPayrollRefreshed?.();
  };

  const assignLocation = async (locationId: string) => {
    const projectId = selection[locationId];
    if (!projectId) {
      addToast({ type: "error", title: "Choose a project first" });
      return;
    }
    if (!organizationId) {
      addToast({ type: "error", title: "No organisation in context" });
      return;
    }
    setSavingId(locationId);
    /* eslint-disable @typescript-eslint/no-explicit-any -- pnl_payroll_location_map is not yet in generated Supabase types */
    const { error } = await (supabase as any)
      .from("pnl_payroll_location_map")
      .update({ claims_project_id: projectId, match_method: "manual" })
      .eq("id", locationId)
      .eq("organization_id", organizationId);
    /* eslint-enable @typescript-eslint/no-explicit-any */
    setSavingId(null);

    if (error) {
      addToast({ type: "error", title: "Could not save mapping", message: error.message });
      return;
    }
    setLocations((prev) => prev.filter((l) => l.id !== locationId));
    addToast({ type: "success", title: "Location mapped" });
  };

  return (
    <div style={pnlChartCardStyle} className="pnl-chart-card">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        style={{
          display: "flex", alignItems: "center", gap: 8, width: "100%",
          background: "none", border: "none", cursor: "pointer", padding: 0,
          font: "inherit", color: "var(--text-primary)", fontWeight: 600, fontSize: "0.95rem",
        }}
      >
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <ShieldAlert size={18} style={{ color: "#f59e0b" }} />
        Admin: unmapped data
        {orgDetails?.name && (
          <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)" }}>
            for {orgDetails.name}
          </span>
        )}
        <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)" }}>
          ({loading ? "loading" : locations.length} locations, {unmappedCategoryStrings.length} categories,{" "}
          {orphansLoading ? "loading" : `${orphans.length} orphaned`})
        </span>
      </button>

      {expanded && (
        <div style={{ marginTop: 16, display: "grid", gap: 20 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600 }}>
                <MapPin size={14} /> Unmapped payroll locations
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={refreshingPayroll || !organizationId}
                onClick={refreshPayroll}
              >
                <RefreshCw size={12} style={{ marginRight: 4 }} />
                {refreshingPayroll ? "Refreshing…" : "Refresh payroll data"}
              </button>
            </div>
            {loading ? (
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Loading…</div>
            ) : locations.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Every project payroll location is mapped.</div>
            ) : (
              <div style={{ display: "grid", gap: 6 }}>
                {locations.map((loc) => (
                  <div key={loc.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border-color-light)" }}>
                    <span style={{ flex: 1, fontSize: 13 }}>{loc.keypay_location_name}</span>
                    <label htmlFor={`pnl-assign-${loc.id}`} style={{ fontSize: 12, color: "var(--text-muted)" }}>Assign to</label>
                    <select
                      id={`pnl-assign-${loc.id}`}
                      className="form-input"
                      value={selection[loc.id] ?? ""}
                      onChange={(e) => setSelection((prev) => ({ ...prev, [loc.id]: e.target.value }))}
                      style={{ padding: "4px 8px", fontSize: 12, width: "auto", minWidth: 160 }}
                    >
                      <option value="">Choose a project…</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={savingId === loc.id || !selection[loc.id]}
                      onClick={() => assignLocation(loc.id)}
                    >
                      {savingId === loc.id ? "Saving…" : "Save"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              <Tag size={14} /> Unmapped category strings
            </div>
            {unmappedCategoryStrings.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Every P&L category string resolves to a metric.</div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {unmappedCategoryStrings.map((name) => (
                  <span
                    key={name}
                    style={{
                      fontSize: 12, padding: "3px 10px", borderRadius: 20,
                      background: "#fef2f2", color: "#991b1b", border: "1px solid rgba(239,68,68,0.2)",
                    }}
                    title="Counted as a deductible Other expense until an alias is created (plan decision D4)."
                  >
                    {name}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              <FileWarning size={14} /> Values with no category
              <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)" }}>
                {orphansLoading ? "loading" : describeOrphanTotals(orphans)}
              </span>
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8, lineHeight: 1.5 }}>
              Numbers typed under a category that is no longer in the organisation's category list. No P&L has a row to
              show them. Clear them on the project's P&L tab, or add the category back so they are visible again.
            </div>
            {orphansLoading ? (
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Loading…</div>
            ) : orphans.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Every value belongs to a configured category.</div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {orphans.map((orphan) => (
                  <div
                    key={orphan.category}
                    style={{
                      padding: "8px 10px", borderRadius: 8,
                      border: `1px solid ${orphan.reachesPnlEngine ? "rgba(239,68,68,0.25)" : "var(--border-color-light)"}`,
                      background: orphan.reachesPnlEngine ? "#fef2f2" : "transparent",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{orphan.category}</span>
                      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        {orphan.rowCount} {orphan.rowCount === 1 ? "value" : "values"}, {orphan.totalLabel},{" "}
                        {orphan.sources.join(" and ")}
                      </span>
                      {orphan.reachesPnlEngine && (
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#991b1b" }}>
                          read by the P&L engine
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 4, lineHeight: 1.6 }}>
                      {orphan.occurrences.join(" · ")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
